import React from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { formatIdsAddress, IdsAddConnectionModal, type IdsEditConnection } from '../components/IdsAddConnectionModal';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import { SettingsStatusChip } from '../components/SettingsStatusChip';
import {
  SETTINGS_BTN_DANGER_OUTLINE,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
} from '../utils/settingsSectionStyles';

type IdsConnection = {
  id: string;
  server: 'shk' | 'open' | 'direct';
  companyId: number;
  companyName: string;
  street: string;
  postalCode: string;
  city: string;
  customerNumberRequired: boolean;
  usernameRequired: boolean;
  passwordRequired: boolean;
  customerNumber: string;
  username: string;
  hasPassword: boolean;
  lastStatusCode: number | null;
  lastStatusMessage: string | null;
  catalog?: 'omd' | null;
};

type IdsProcessSnap = {
  connection_id: string;
  process_code: string;
  version: string;
  teilprozesse: unknown;
  url?: string | null;
  last_import_status?: string | null;
  last_import_error?: string | null;
  last_import_stats?: { imported?: number; overlay?: number } | null;
};

export function IdsSettingsSection() {
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [headerNotice, setHeaderNotice] = React.useState<string | null>(null);
  const [rowNotice, setRowNotice] = React.useState<{ id: string; text: string } | null>(null);
  const [softwareConfigured, setSoftwareConfigured] = React.useState(false);
  const [canManage, setCanManage] = React.useState(false);
  const [connections, setConnections] = React.useState<IdsConnection[]>([]);
  const [processes, setProcesses] = React.useState<IdsProcessSnap[]>([]);
  const [defaultPostalCode, setDefaultPostalCode] = React.useState('');
  const [directShops, setDirectShops] = React.useState<Array<{ id: number; name: string; catalog?: 'omd' }>>([]);
  const [addOpen, setAddOpen] = React.useState(false);
  const [editConnection, setEditConnection] = React.useState<IdsEditConnection | null>(null);
  const [testingId, setTestingId] = React.useState<string | null>(null);

  const load = React.useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch('/api/settings/ids', { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || 'IDS-Einstellungen konnten nicht geladen werden.');
      setSoftwareConfigured(Boolean(d.softwareConfigured));
      setCanManage(Boolean(d.canManage));
      setDefaultPostalCode(String(d.defaultPostalCode ?? ''));
      setDirectShops(Array.isArray(d.directShops) ? d.directShops : []);
      setConnections(Array.isArray(d.connections) ? d.connections : []);
      setProcesses(Array.isArray(d.processes) ? d.processes : []);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const testConnection = async (id: string) => {
    setTestingId(id);
    setActionError(null);
    setHeaderNotice(null);
    setRowNotice(null);
    try {
      const res = await fetch(`/api/settings/ids/connections/${encodeURIComponent(id)}/test`, {
        method: 'POST',
        credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || 'Prüfung fehlgeschlagen.');
      setRowNotice({ id, text: 'Geprüft.' });
      await load({ silent: true });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setTestingId(null);
    }
  };

  const removeConnection = async (id: string) => {
    if (!window.confirm('Verbindung wirklich löschen?')) return;
    setActionError(null);
    setHeaderNotice(null);
    setRowNotice(null);
    try {
      const res = await fetch(`/api/settings/ids/connections/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || 'Löschen fehlgeschlagen.');
      await load({ silent: true });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    }
  };

  const snapsFor = (id: string) => processes.filter((p) => p.connection_id === id);

  return (
    <>
      <SettingsCard
        title="Verbindungen"
        actions={
          <>
            <SettingsCardSavedStatus saved={Boolean(headerNotice)}>{headerNotice}</SettingsCardSavedStatus>
            {canManage && softwareConfigured && !loading ? (
              <button
                type="button"
                className={SETTINGS_BTN_PRIMARY}
                onClick={() => {
                  setEditConnection(null);
                  setAddOpen(true);
                }}
              >
                <Plus className="w-4 h-4" strokeWidth={1.75} />
                Hinzufügen
              </button>
            ) : null}
          </>
        }
      >
        <div className="space-y-5">
          {loading ? (
            <InlineLoadingState label="Einstellungen werden geladen …" className="justify-start" />
          ) : (
            <>
              {loadError ? (
                <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{loadError}</div>
              ) : null}
              {!softwareConfigured ? (
                <p className="text-sm text-app-text-subtle">
                  Die IDS-Anbindung ist serverseitig noch nicht eingerichtet. Bitte den Meisterpfad-Support kontaktieren.
                </p>
              ) : (
                <>
                  {actionError ? (
                    <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{actionError}</div>
                  ) : null}

                  {!canManage ? (
                    <p className="text-sm text-app-text-subtle">Nur Inhaber oder Administratoren können Verbindungen anlegen.</p>
                  ) : null}

                <div className="space-y-3">
                  <h3 className={SETTINGS_FIELD_LABEL}>Gespeicherte Verbindungen</h3>
                  {connections.length === 0 ? (
                    <p className="text-sm text-app-text-subtle">Noch keine Verbindung.</p>
                  ) : (
                    connections.map((c) => {
                      const snaps = snapsFor(c.id);
                      const std = snaps.find((s) => s.process_code === 'STD');
                      const sha = snaps.find((s) => s.process_code === 'SHA');
                      const omd = snaps.find((s) => s.process_code === 'OMD-oauth');
                      const testedOk = c.lastStatusCode === 0;
                      const testedFail = c.lastStatusCode != null && c.lastStatusCode !== 0;
                      const imported = Number(std?.last_import_stats?.imported ?? 0);
                      const address = formatIdsAddress(c);
                      const direct = c.server === 'direct'
                        ? directShops.find((s) => Number(s.id) === Number(c.companyId))
                        : undefined;
                      const displayName = direct?.name ?? c.companyName;
                      const hasOmdCatalog = c.catalog === 'omd' || direct?.catalog === 'omd';
                      const datanormChip = std
                        ? std.last_import_status === 'error'
                          ? <SettingsStatusChip tone="err">DATANORM fehlgeschlagen</SettingsStatusChip>
                          : imported > 0
                            ? <SettingsStatusChip tone="ok">DATANORM · {imported}</SettingsStatusChip>
                            : <SettingsStatusChip tone="ok">DATANORM</SettingsStatusChip>
                        : c.server === 'direct'
                            ? null
                            : snaps.length > 0
                              ? <SettingsStatusChip tone="muted">Kein DATANORM</SettingsStatusChip>
                              : null;
                      const omdChip = omd
                        ? omd.last_import_status === 'error'
                          ? <SettingsStatusChip tone="err">OMD fehlgeschlagen</SettingsStatusChip>
                          : omd.last_import_status === 'ok'
                            ? <SettingsStatusChip tone="ok">OMD · verbunden</SettingsStatusChip>
                            : <SettingsStatusChip tone="ok">OMD</SettingsStatusChip>
                        : hasOmdCatalog
                          ? <SettingsStatusChip tone="ok">OMD</SettingsStatusChip>
                          : null;
                      return (
                        <div key={c.id} className="rounded-md border border-app-border px-3 py-2">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                            <div className="min-w-0 sm:flex-1">
                              <p className="text-sm font-medium text-app-text leading-5 truncate">{displayName}</p>
                              {address ? (
                                <p className="text-xs text-app-text-subtle mt-0.5 truncate">{address}</p>
                              ) : null}
                            </div>
                            <div className="flex items-center justify-between gap-3 sm:justify-end">
                              <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                                {snaps.length === 0 && !hasOmdCatalog ? (
                                  <SettingsStatusChip tone="muted">Noch nicht geprüft</SettingsStatusChip>
                                ) : (
                                  <>
                                    {sha?.url ? <SettingsStatusChip tone="ok">Shop</SettingsStatusChip> : snaps.length > 0 ? <SettingsStatusChip tone="muted">Kein Shop</SettingsStatusChip> : null}
                                    {datanormChip}
                                    {omdChip}
                                    {testedOk ? <SettingsStatusChip tone="ok">Geprüft</SettingsStatusChip> : null}
                                    {testedFail ? (
                                      <SettingsStatusChip tone="err">{c.lastStatusMessage || 'Prüfung fehlgeschlagen'}</SettingsStatusChip>
                                    ) : null}
                                  </>
                                )}
                              </div>
                              {canManage ? (
                                <div className="flex items-center gap-2 shrink-0">
                                  {rowNotice?.id === c.id ? (
                                    <span className="text-sm text-emerald-600 font-medium">{rowNotice.text}</span>
                                  ) : null}
                                  <button
                                    type="button"
                                    className={SETTINGS_BTN_OUTLINE}
                                    onClick={() => void testConnection(c.id)}
                                    disabled={testingId === c.id}
                                  >
                                    {testingId === c.id ? <LoadingSpinner size="sm" /> : null}
                                    Prüfen
                                  </button>
                                  <button
                                    type="button"
                                    className={`${SETTINGS_BTN_OUTLINE} !px-2`}
                                    onClick={() => {
                                      setEditConnection({
                                        id: c.id,
                                        server: c.server,
                                        companyId: c.companyId,
                                        companyName: displayName,
                                        street: c.street,
                                        postalCode: c.postalCode,
                                        city: c.city,
                                        customerNumberRequired: c.customerNumberRequired,
                                        usernameRequired: c.usernameRequired,
                                        passwordRequired: c.passwordRequired,
                                        customerNumber: c.customerNumber,
                                        username: c.username,
                                        hasPassword: c.hasPassword,
                                      });
                                      setAddOpen(true);
                                    }}
                                    title="Zugang bearbeiten"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    className={`${SETTINGS_BTN_DANGER_OUTLINE} !px-2`}
                                    onClick={() => void removeConnection(c.id)}
                                    title="Verbindung löschen"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </>
        )}
        </div>
      </SettingsCard>

      <IdsAddConnectionModal
        open={addOpen}
        defaultPostalCode={defaultPostalCode}
        directShops={directShops}
        connectedDirectIds={connections.filter((c) => c.server === 'direct').map((c) => c.companyId)}
        editConnection={editConnection}
        onClose={() => {
          setAddOpen(false);
          setEditConnection(null);
        }}
        onSaved={async (result) => {
          setAddOpen(false);
          setEditConnection(null);
          await load({ silent: true });
          if (result.testedOk) {
            setActionError(null);
            setRowNotice(null);
            setHeaderNotice('Gespeichert.');
          } else {
            setHeaderNotice(null);
            setActionError(result.testError || 'Großhändler gespeichert, Prüfung fehlgeschlagen.');
          }
        }}
      />
    </>
  );
}
